// The dealer application frame: mounts the shared AppShell (sidebar + topbar +
// page), feeds it the nav model, logo, account badge, and bar slots, and hosts
// the app-level providers, effects, and modals that live outside the shell (the
// auth/config providers, the default-rooftop config bootstrap, and the disabled-bypass overlay). The shell owns collapse/width/drawer
// state; this frame owns the data.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import { useTheme, type Theme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { reportRooftopLoadFailed } from '@features/auth/services/profileReport';

import { AppShell, AppShellSidebarAccountBadge, BrandLogo } from '@hopdrive/feature-app-shell';
import { isAdminViewerRole } from '@hopdrive/feature-order-index';
import { DealerPlannerHost } from '@features/moveCreation/plannerHost';
import { PortalHostProvider, type PortalHostValue, useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { ProductShellExperience } from '@hopdrive/feature-product-shell';
import { SettingsPanel } from '@hopdrive/feature-settings-ui';
import { createConfigServiceAdapter } from '@hopdrive/feature-settings-ui/adapter';
import { dealerSettingsHostServices } from '@features/products/settingsHost/settingsHostServices';
import { toast } from 'react-toastify';

import {
  useUser,
  useRooftop,
  useCustomerConfig,
  usePermissions,
  usePortalProductsState,
  useProductsLoaded,
  setRooftop,
  setCustomerConfig,
  setCustomerConfigLoaded,
} from '@store';
import { useGetDefaultRooftopLazyQuery } from './queries/getDefaultRooftop.query.generated';
import { checkForPermittedRole, checkForPowerRole, authService } from '@features/auth/services/authService';
import { useFeaturePermission, usePermissionsLoaded } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';

import { resolveNavModel } from './nav/resolveNavModel';
import { useMissingRefBadgeCount } from '@features/missingRefReport/hooks/useMissingRefBadgeCount';
import { isFullBleedPath, resolveScrollMode } from './nav/resolveScrollMode';
import { configServicePort, fetchLocalInputs, fetchProductAccess } from '@features/products/apolloFetchers';
import { toViewerPermissions } from '@features/products/toViewerPermissions';
import { SALES_MAILTO } from '@features/products/ui/productCopy';
import { REACT_ROUTER_PORT } from '@features/products/reactRouterPort';
import { PRODUCT_ROUTES } from '@features/products/productRoutes';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';

import { ChatModalProvider } from '@components/Chat/ChatModalManager';
import { BypassTrapOverlay } from '@components/DisabledBypassTrap';
import GlobalSmsSubscriptionProvider from '@features/moves/components/GlobalSmsSubscriptionProvider';
import PermissionsPollingProvider from '@features/auth/providers/PermissionsPollingProvider';
import FeatureConfigLoader from '@features/auth/providers/FeatureConfigLoader';
import RoleChip from '@features/userManagement/components/RoleChip';
import { shortVersion } from '@components/BuildBadge';
import { HeaderSearch } from '@features/globalSearch';
import HeaderRooftopSelect from '@features/layout/components/HeaderRooftopSelect';
import UnreadMessagesDropdown from '@features/moves/components/UnreadMessagesDropdown';
import { ACTION_ROUTES } from '@features/products/actionRoutes';

interface DealerAppFrameProps {
  children: React.ReactNode;
}

const DealerAppFrame = ({ children }: DealerAppFrameProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const location = useLocation();
  const navigate = useNavigate();
  const go = (route: string) => navigate(route);

  const user = useUser();
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();

  const isPermitted = checkForPermittedRole(user);
  const hasAdminPrivileges = checkForPowerRole(user);
  const shouldShowFullLayout = user && isPermitted === true;

  // Feature permissions, pre-resolved into the shape resolveNavModel consumes. Memoized on the
  // grants so the object is stable across renders for the products hook.
  const canPlanMoves = useFeaturePermission(FeatureName.PLANNING);
  const canManageUsers = useFeaturePermission(FeatureName.USER_MANAGEMENT);
  const canManageDrivers = useFeaturePermission(FeatureName.DRIVER_MANAGEMENT);
  const canViewInvoices = useFeaturePermission(FeatureName.INVOICES);
  const canViewAppraisals = useFeaturePermission(FeatureName.APPRAISALS);
  const canViewMobileService = useFeaturePermission(FeatureName.MOBILE_SERVICE);
  const canViewPartsDelivery = useFeaturePermission(FeatureName.PARTS_DELIVERY);
  const permissions = React.useMemo(
    () => ({ canPlanMoves, canManageUsers, canManageDrivers, canViewInvoices, canViewAppraisals, canViewMobileService, canViewPartsDelivery }),
    [canPlanMoves, canManageUsers, canManageDrivers, canViewInvoices, canViewAppraisals, canViewMobileService, canViewPartsDelivery],
  );

  // The portal-products host: every app-specific value the products feature needs, in one
  // memoized object. Mounting <PortalHostProvider> with it is the whole wiring — the provider
  // owns the per-rooftop fetch (snapshot + local inputs) and the re-derive on permission change,
  // and publishes the result through the package's store for the nav (below) and the route table.
  const grants = usePermissions();
  const permissionsLoaded = usePermissionsLoaded();
  const grantedFeatures = React.useMemo(() => new Set(grants), [grants]);
  const productPermissions = React.useMemo(() => toViewerPermissions(permissions), [permissions]);
  const portalHost = React.useMemo<PortalHostValue>(
    () => ({
      viewer: {
        role: user?.profile?.default_role ?? null,
        isAdminViewer: isAdminViewerRole(user?.profile?.default_role),
        customerId: rooftop?.id,
        // Keys the persisted product snapshot with the rooftop, so the next boot paints this
        // user's last menu for this rooftop and never another user's or another rooftop's.
        viewerId: user?.profile?.id,
        actorLabel: user?.profile?.email ?? '',
        grantedFeatures,
        permissionsLoaded,
        productPermissions,
      },
      fetchers: { fetchProductAccess, fetchLocalInputs },
      settings: { request: configServicePort, createAdapter: createConfigServiceAdapter, hostServices: dealerSettingsHostServices },
      routing: { isFullBleedPath, actionRoutes: ACTION_ROUTES, routes: PRODUCT_ROUTES, salesMailto: SALES_MAILTO },
      router: REACT_ROUTER_PORT,
      components: { Fallback: DefaultFallback, ProductShell: ProductShellExperience, SettingsPanel },
      notify: {
        info: message => toast.info(message, { autoClose: 2000, toastId: 'refetch' }),
        error: message => toast.error(message, { toastId: 'refetch-error' }),
      },
      // The products load reports a signed-in viewer whose customer never arrives and a failed
      // snapshot here. Both leave a dealer with no products in front of them, so both are errors.
      // A failed refresh behind a stored snapshot leaves products on screen: a warning.
      report: (event, data, error) =>
        Sentry.captureMessage(event, {
          level: event === 'portal_products.refresh_failed' ? 'warning' : 'error',
          tags: { feature: 'portal-products', event },
          extra: { ...data, errorName: error instanceof Error ? error.name : undefined, errorMessage: error instanceof Error ? error.message : undefined },
        }),
    }),
    [user, rooftop?.id, grantedFeatures, permissionsLoaded, productPermissions],
  );

  const products = usePortalProductsState();
  const productsLoaded = useProductsLoaded();

  // Rooftop loading: true until the rooftop record arrives, with a 3s ceiling so
  // the tier-dependent nav entries don't hang on a stuck load.
  const [rooftopLoading, setRooftopLoading] = React.useState(true);
  React.useEffect(() => {
    if (user && rooftop) {
      setRooftopLoading(false);
    } else if (user) {
      const timeout = setTimeout(() => setRooftopLoading(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [user, rooftop]);

  // Default-rooftop config bootstrap: populates rooftop / customerConfig /
  // customerConfigLoaded so config-gated routes resolve.
  const [getRooftop] = useGetDefaultRooftopLazyQuery();
  const fetchAndSetDefaultRooftop = async (customerId: number, customerIdSource: 'pinned' | 'profile') => {
    const report = (outcome: 'request_failed' | 'empty', extra: { error?: unknown; graphQLErrors?: Array<{ message?: string; code?: unknown }>; networkStatus?: number } = {}) =>
      reportRooftopLoadFailed({
        source: 'default_rooftop',
        outcome,
        operation: 'GetDefaultRooftop',
        variables: { customerId },
        role: user?.profile?.default_role,
        userId: user?.profile?.id,
        customerIdSource,
        ...extra,
      });
    try {
      const res = await getRooftop({ variables: { customerId } });
      const rooftopData = res?.data?.customers_by_pk;
      // Apollo's lazy query resolves with `error` rather than throwing.
      if (res?.error) {
        report('request_failed', {
          error: res.error,
          graphQLErrors: res.error.graphQLErrors?.map(e => ({ message: e.message, code: e.extensions?.code })),
          networkStatus: (res.error.networkError as { statusCode?: number } | null)?.statusCode,
        });
      } else if (!rooftopData) {
        report('empty');
      }
      setRooftop((rooftopData as any) || undefined);
      if (rooftopData?.effective_config) {
        setCustomerConfig(rooftopData.effective_config);
      }
    } catch (err) {
      report('request_failed', { error: err });
    } finally {
      setCustomerConfigLoaded(true);
    }
  };
  React.useEffect(() => {
    if (user?.profile?.customer_id && !rooftop) {
      const storedRooftopId = localStorage?.getItem('rooftopId');
      const rooftopIdToUse = storedRooftopId ? parseInt(storedRooftopId, 10) : user.profile.customer_id;
      fetchAndSetDefaultRooftop(rooftopIdToUse, storedRooftopId ? 'pinned' : 'profile');
    } else if (user && !user?.profile?.customer_id && !checkForPowerRole(user)) {
      // Logged in, no customer, not a power role → nothing will load a config, so settle now.
      // Power roles settle via HeaderRooftopSelect when their config lands.
      setCustomerConfigLoaded(true);
    }
  }, [user]);

  // Self-managed-capable: drives the SMS subscription and the nav's self-managed gates.
  const isSelfManaged = useValetSelfManaged();

  const handleLogout = async () => {
    try {
      await authService.writeUserEvent('user.logout');
    } catch (err) {
      console.log('Failed to write user event', err);
    }
    await authService.logout();
    go('/login');
  };

  const missingRefCount = useMissingRefBadgeCount({
    enabled: Boolean(customerConfig?.reference_num_report_enabled),
    customerId: rooftop?.id || user?.profile?.customer_id,
    products,
  });

  const sections = resolveNavModel({
    user,
    products,
    rooftop,
    customerConfig,
    permissions,
    rooftopLoading,
    isSelfManaged,
    missingRefCount,
  });

  const accountBadge = user ? (
    <AppShellSidebarAccountBadge
      user={{
        name: user.profile?.display_name ?? undefined,
        email: user.profile?.email ?? undefined,
        avatarUrl: user.profile?.avatar_url ?? undefined,
      }}
      roleBadge={<RoleChip role={user.profile?.default_role || ''} />}
      environment={import.meta.env.VITE_ENV}
      onProfile={() => go('/profile')}
      onSupport={() => go('/support')}
      onLogout={handleLogout}
    />
  ) : undefined;

  // The shell's trailing slot packs tight (gap 4px) and lets items shrink; give
  // the bar controls their own spacing and fixed widths so they don't squish.
  const barContent = user ? (
    <div className={cls.barSlots}>
      <HeaderSearch />
      {hasAdminPrivileges ? (
        <div className={cls.rooftopSlot}>
          <HeaderRooftopSelect />
        </div>
      ) : null}
      {user.profile?.default_role !== 'admin' && isSelfManaged ? <UnreadMessagesDropdown /> : null}
    </div>
  ) : undefined;

  const shellElement = shouldShowFullLayout ? (
    <>
      <AppShell
        sections={sections}
        currentPath={location.pathname}
        navigate={go}
        navPending={rooftopLoading || !productsLoaded}
        logo={<BrandLogo />}
        condensedLogo={<BrandLogo condensed />}
        onBrandClick={() => go('/')}
        sidebarFooter={accountBadge}
        appName=""
        barContent={barContent}
        scroll={resolveScrollMode(location.pathname)}
        storageKey="dealer-shell"
      >
        {children}
      </AppShell>
      {/* Subtle build marker, tucked into the page's bottom-right corner. */}
      <span className={cls.pageVersion}>{shortVersion()}</span>
    </>
  ) : (
    // Unauthenticated / not-yet-permitted: page + toasts only, no chrome.
    <AppShell sections={sections} chromeless scroll="page">
      {children}
    </AppShell>
  );

  return (
    <PortalHostProvider value={portalHost}>
      {/* The Move Planner's host, for the planner and for the planner pieces other surfaces render. */}
      <DealerPlannerHost>
      <ChatModalProvider>
      <PermissionsPollingProvider>
        <FeatureConfigLoader>
          {/* Always mounted: the provider gates its own subscription on the self-managed model,
              and swapping the element type here would remount the whole shell when the model
              resolves. */}
          <GlobalSmsSubscriptionProvider>
            <div className={cls.viewport}>{shellElement}</div>
          </GlobalSmsSubscriptionProvider>

          <BypassTrapOverlay />
        </FeatureConfigLoader>
      </PermissionsPollingProvider>
      </ChatModalProvider>
      </DealerPlannerHost>
    </PortalHostProvider>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  // The shell fills its mount; give it a full-viewport box to fill.
  viewport: css`
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
  `,
  // Subtle build marker tucked hard into the page's bottom-right corner — small,
  // low-contrast, and click-through so it stays out of the way. Below chrome/modals.
  pageVersion: css`
    position: fixed;
    right: 3px;
    bottom: 2px;
    z-index: ${theme.zIndex.appBar - 1};
    font-size: 9px;
    line-height: 1;
    color: ${theme.palette.text.disabled};
    pointer-events: none;
    user-select: none;
  `,
  // Topbar controls: comfortable spacing, and none of them shrink below their
  // intended widths (the search is a fixed 300px, the rooftop select 360px).
  barSlots: css`
    display: flex;
    align-items: center;
    gap: 16px;
    & > * {
      flex-shrink: 0;
    }
  `,
  rooftopSlot: css`
    width: 360px;
  `,
});

export default DealerAppFrame;
